import { clsx } from 'clsx'

import { Avatar, Flex, Text, VisuallyHidden } from '@buffer-mono/popcorn'

import { getBubbleContent, getSendStatus } from './threadDisplay'
import type { SendStatus } from './threadDisplay'
import type { ThreadMessageData } from '../fragments/threadMessage'

import styles from './MessageThread.module.css'

import type { JSX } from 'react'

type MessageBubbleProps = {
  message: ThreadMessageData
  /**
   * Whether this bubble ends a run of inbound messages and so carries the
   * sender's avatar. Inbound bubbles before it leave the slot empty so the
   * run stays aligned; outbound bubbles have no slot at all.
   */
  showAvatar: boolean
  /** Whether this is the thread's latest message, which carries the send status. */
  isLatest: boolean
}

const SEND_STATUS_TEXT: Record<SendStatus, string> = {
  sending: 'Sending…',
  sent: 'Sent',
  failed: 'Message not sent',
}

/**
 * One message, per the DMs Figma `_DM message`: inbound on the left in the
 * sunken surface, outbound on the right in the brand tint. Reactions sit in a
 * pill over the bubble's bottom corner, on the side away from the avatar.
 *
 * Text and non-text content go through `getBubbleContent`, so an image, an
 * unsent message and content the thread cannot show yet all render from the
 * same three shapes. A reply of ours the network rejected keeps its bubble
 * and says "Message not sent" beneath it, matching the conversation list;
 * the latest message, when it is ours, says "Sending…" or "Sent" there.
 */
export function MessageBubble({
  message,
  showAvatar,
  isLatest,
}: MessageBubbleProps): JSX.Element {
  const content = getBubbleContent(message)
  const sendStatus = getSendStatus(message, isLatest)
  const isOwn = message.isOwnMessage
  const author = message.author
  const senderName = author.name ?? author.username ?? 'Unknown'
  // Every reaction the API returns, in order; the same emoji can repeat.
  const reactions = message.reactions
    .map((reaction) => reaction.emoji)
    .join(' ')

  return (
    <Flex
      as="li"
      align="end"
      justify={isOwn ? 'end' : 'start'}
      gap="xs"
      className={clsx(isOwn && styles.own, reactions && styles.reacted)}
    >
      {!isOwn && (
        <Flex as="span" className={styles.avatarSlot}>
          {showAvatar && (
            <Avatar
              src={author.avatar}
              alt=""
              fallback="letter"
              fallbackName={senderName}
              size="xsmall"
            />
          )}
        </Flex>
      )}
      <Flex
        as="span"
        direction="column"
        align={isOwn ? 'end' : 'start'}
        gap="3xs"
        className={styles.bubbleColumn}
      >
        <span className={styles.bubbleAnchor}>
          <Flex
            as="span"
            direction="column"
            gap="xs"
            className={clsx(
              styles.bubble,
              content.kind === 'placeholder' && styles.placeholder,
            )}
          >
            <VisuallyHidden>{isOwn ? 'You:' : `${senderName}:`}</VisuallyHidden>
            {content.kind === 'image' ? (
              <>
                {content.text && <Text as="span">{content.text}</Text>}
                <img
                  className={styles.image}
                  src={content.url}
                  alt={`Attachment from ${isOwn ? 'you' : senderName}`}
                  width={content.width ?? undefined}
                  height={content.height ?? undefined}
                  loading="lazy"
                />
              </>
            ) : (
              <Text
                as="span"
                color={content.kind === 'placeholder' ? 'subtle' : 'default'}
              >
                {content.text}
              </Text>
            )}
          </Flex>
          {reactions && (
            <Flex as="span" align="center" className={styles.reactions}>
              <span aria-hidden>{reactions}</span>
              <VisuallyHidden>{`Reactions: ${reactions}`}</VisuallyHidden>
            </Flex>
          )}
        </span>
        {sendStatus && (
          <Text
            as="span"
            size="sm"
            color={sendStatus === 'failed' ? undefined : 'subtle'}
            className={clsx(sendStatus === 'failed' && styles.notSent)}
          >
            {SEND_STATUS_TEXT[sendStatus]}
          </Text>
        )}
      </Flex>
    </Flex>
  )
}
